import React, { useState, useEffect } from 'react';
import { FaSyncAlt, FaSearch, FaSortUp, FaSortDown, FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { Auth } from 'aws-amplify';
import Pagination from './Pagination';
import CreateNumber from './CreateNumber';
import EditNumber from './EditStaticNumbers';
import Acknowledgment from './Acknowledgment';
import './LoadingScreen.css';

const StaticNumberPage = () => {
  const [staticNumbers, setStaticNumbers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateNumberOpen, setIsCreateNumberOpen] = useState(false);
  const [acknowledgment, setAcknowledgment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'phno_campaign', direction: 'asc' });
  const [editingNumber, setEditingNumber] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);


  const get_phno = process.env.REACT_APP_get_phno;

  useEffect(() => {
    const fetchStaticNumbers = async () => {
      setIsLoading(true);
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const apiUrl = `${get_phno}?phno_type=static`;

        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch static numbers');
        }

        const data = await response.json();
        setStaticNumbers(data.staticNumbers);
      } catch (error) {
        console.log('Error fetching static numbers:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStaticNumbers();
  }, [get_phno]);

  const handleStatusToggle = (index) => {
    const updatedStaticNumbers = staticNumbers.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          status: !item.status,
        };
      }
      return item;
    });
    setStaticNumbers(updatedStaticNumbers);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const apiUrl = `${get_phno}?phno_type=static`;

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch static numbers');
      }

      const data = await response.json();
      setStaticNumbers(data.staticNumbers);
    } catch (error) {
      console.log('Error fetching static numbers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNumberClick = () => {
    setIsCreateNumberOpen(true);
  };

  const handleCreateNumberClose = () => {
    setIsCreateNumberOpen(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedStaticNumbers = [...staticNumbers].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const sanitizeString = (str) => str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  const filteredNumbers = sortedStaticNumbers.filter((item) => {
    const sanitizedQuery = sanitizeString(searchQuery);

    return (
      sanitizeString(item.phno_campaign).includes(sanitizedQuery) ||
      sanitizeString(item.international).includes(sanitizedQuery) ||
      (item.phno_description && sanitizeString(item.phno_description).includes(sanitizedQuery)) ||
      (item.created_by && sanitizeString(item.created_by).includes(sanitizedQuery)) ||
      (item.created_on && sanitizeString(item.created_on).includes(sanitizedQuery))
    );
  });


  const currentItems = filteredNumbers.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleAcknowledgment = (acknowledgmentMessage) => {
    setAcknowledgment({ ...acknowledgmentMessage, sourcePage: 'staticNumberPage' });
  };

  const handleAcknowledgmentClose = () => {
    setAcknowledgment(null);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <FaSortUp className="text-blue-500 cursor-pointer" /> : <FaSortDown className="text-blue-500 cursor-pointer" />;
    }
    return <FaSortUp className="text-blue-500 opacity-50 cursor-pointer" />;
  };

  const handleEditClick = (pk) => {
    const editedNumber = staticNumbers.find((num) => num.pk === pk);
    if (editedNumber) {
      setEditingNumber(editedNumber);
      setIsEditOpen(true);
    } else {
      console.log(`Error: Number with pk ${pk} not found.`);
    }
  };



  const handleUpdateNumber = (updatedNumber) => {
    const updatedNumbers = staticNumbers.map((num) =>
      num.pk === updatedNumber.pk ? updatedNumber : num
    );
    setStaticNumbers(updatedNumbers);
  };

  return (
    <div>
      <div className="flex justify-end items-center mb-4">
        <div className="relative flex items-center mr-2">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Campaign Name"
            className="px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          />
          <FaSearch className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500" />
        </div>
        <div className="flex">
          <button
            className="bg-gray-200 text-gray-600 rounded hover:bg-gray-300 p-2 focus:outline-none"
            onClick={handleRefresh}
          >
            <FaSyncAlt className="w-4 h-4" />
          </button>
          <button
            className="ml-2 bg-blue-500 text-white rounded hover:bg-blue-600 p-2 focus:outline-none"
            onClick={handleCreateNumberClick}
          >
            Create Number
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="pinwheel">
            <div className="pinwheel__line"></div>
            <div className="pinwheel__line"></div>
            <div className="pinwheel__line"></div>
            <div className="pinwheel__line"></div>
            <div className="pinwheel__line"></div>
            <div className="pinwheel__line"></div>
          </div>
        ) : (
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
                <tr>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('phno_campaign')}>
                    <div className="flex items-center gap-x-1">
                      <span>Campaign Name</span>
                      {getSortIcon('phno_campaign')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('international')}>
                    <div className="flex items-center gap-x-1">
                      <span>Tracking Number</span>
                      {getSortIcon('international')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('phno_description')}>
                    <div className="flex items-center gap-x-1">
                      <span>Description</span>
                      {getSortIcon('phno_description')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('created_by')}>
                    <div className="flex items-center gap-x-1">
                      <span>Created By</span>
                      {getSortIcon('created_by')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('created_on')}>
                    <div className="flex items-center gap-x-1">
                      <span>Created At</span>
                      {getSortIcon('created_on')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('forward_phone_number')}>
                    <div className="flex items-center gap-x-1">
                      <span>Forward Number</span>
                      {getSortIcon('forward_phone_number')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b cursor-pointer" onClick={() => handleSort('source')}>
                    <div className="flex items-center gap-x-1">
                      <span>Source</span>
                      {getSortIcon('source')}
                    </div>
                  </th>
                  <th className="py-2 px-4 text-left border-b">
                    Status
                  </th>
                  <th className="py-2 px-1 text-left border-b"></th>
                  <th className="py-2 px-1 text-left border-b"></th>
                </tr>
              </thead>
            <tbody>
              {currentItems.map((item, index) => (
                <tr key={item.pk} className="hover:bg-gray-50 group">
                  <td className="py-3 px-4 border-b">{item.phno_campaign}</td>
                  <td className="py-3 px-4 border-b">{item.international}</td>
                  <td className="py-3 px-4 border-b">{item.phno_description}</td>
                  <td className="py-3 px-4 border-b">{item.created_by || '-'}</td>
                  <td className="py-3 px-4 border-b">{item.created_on || '-'}</td>
                  <td className="py-3 px-4 border-b">{item.forward_phone_number|| '-'}</td>
                  <td className="py-3 px-4 border-b">{item.source|| '-'}</td>
                  <td className="py-3 px-4 border-b">
                    <button
                      className={`${item.phno_status ? 'bg-green-500' : 'bg-red-500 hover:bg-opacity-80'
                        } text-white px-4 py-2 rounded-full`}
                      onClick={() => handleStatusToggle(index)}
                    >
                      {item.phno_status ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-3 px-1 border-b">
                    <div className="invisible group-hover:visible">
                      <FaPencilAlt
                        className="cursor-pointer text-gray-600 hover:text-blue-500"
                        onClick={() => handleEditClick(item.pk)}
                      />
                    </div>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!isLoading && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredNumbers.length}
          currentPage={currentPage}
          paginate={paginate}
        />
      )}
      {isCreateNumberOpen && (
        <CreateNumber
          onClose={handleCreateNumberClose}
          setAcknowledgment={handleAcknowledgment}
        />
      )}
      {acknowledgment && (
        <Acknowledgment
          acknowledgment={acknowledgment}
          sourcePage="staticNumberPage"
          onClose={handleAcknowledgmentClose}
        />
      )}
      {isEditOpen && (
        <EditNumber
          numberToEdit={editingNumber}
          onClose={() => setIsEditOpen(false)}
          onUpdate={handleUpdateNumber}
        />
      )}
    </div>
  );
};

export default StaticNumberPage;
